import i18n from 'i18next';
import { logTrace, logError } from '../../services/loggerService';
import { useAuthStore } from '../../store/useAuthStore';
import { isSupportedLanguage } from '../../i18n/config';
import { CSRF_COOKIE_NAME, SESSION_MODE } from '../../services/sessionCookieNames';
import { ApiError, parseErrorBody, userMessageFor } from './errors';

/**
 * The admin app's single HTTP pipeline. Ported from hp-app-core-react's
 * apiService with three deliberate changes (FE plan §3.3):
 *
 *   - ONE request path for JSON, multipart and blob bodies (core duplicated
 *     the refresh/CSRF/error logic in postFormData).
 *   - Failures reject with a typed ApiError.
 *   - The client never decides whether to show the error modal. That is the
 *     QueryCache/MutationCache default in app/queryClient.ts, and a query or
 *     mutation opts out with `meta: { silent: true }`. Core's hard-coded
 *     status+endpoint exception list is gone.
 *
 * Kept from core: cookie session + per-app CSRF header, single-flight
 * refresh on 401 and on 403 CSRF_INVALID, soft vs hard logout, 503 → the
 * service-unavailable screen, network failure → the no-network screen.
 */

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api';
const X_API_KEY = import.meta.env.VITE_X_API_KEY || '';

export const isCookieMode = SESSION_MODE === 'cookie';

const csrfCookieValueRegex = new RegExp(
  `(?:^|;\\s*)${CSRF_COOKIE_NAME.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}=([^;]*)`,
);

// State that persists as long as the user is logged in (localStorage mode only).
let jwt: string | null = null;
let activeProfileId: string | null = null;

let tokenRefreshHandler: (() => Promise<boolean>) | null = null;
let refreshInProgress: Promise<RefreshResult> | null = null;
let logoutInProgress = false;

type RefreshResult = 'ok' | 'no_refresh' | 'failed';

export function setJWT(token: string | null) {
  if (!isCookieMode) jwt = token;
}
export function getJWT(): string | null {
  return jwt;
}
export function setActiveProfileId(profileId: string | null) {
  activeProfileId = profileId;
}
export function getActiveProfileId(): string | null {
  return activeProfileId;
}
export function clearAuthState() {
  jwt = null;
  activeProfileId = null;
}
export function setTokenRefreshHandler(handler: (() => Promise<boolean>) | null) {
  tokenRefreshHandler = handler;
}

/**
 * Set while a logout runs (user- or auth-failure-initiated). Every 401 / CSRF
 * 403 in that window is expected — the session is being torn down — so none
 * of them may start a refresh or another logout.
 */
let deviceId: string | null = null;

/**
 * The device's id (Capacitor `Device.getId()`), sent as `X-Device-Id` on every
 * call: the BE gates /admin/* on this device having passed the SMS code (admin
 * MFA). Set by deviceService once the device is collected — a setter rather
 * than an import, because deviceService already imports this client.
 */
export function setDeviceIdHeader(id: string | null) {
  deviceId = id;
}

export function setLogoutInProgress(value: boolean) {
  logoutInProgress = value;
}

function getCsrfToken(): string {
  if (typeof document === 'undefined') return '';
  const match = document.cookie.match(csrfCookieValueRegex);
  return match?.[1] ? decodeURIComponent(match[1]) : '';
}

// --- logout on unrecoverable auth failure ----------------------------------
// Dynamic import avoids the authService ↔ client circular dependency.

async function triggerAuthFailureLogout(soft: boolean): Promise<void> {
  if (logoutInProgress) return;
  logoutInProgress = true;
  try {
    const auth = await import('../../services/authService');
    await (soft ? auth.logoutSoft() : auth.logout());
  } catch {
    if (!soft && typeof window !== 'undefined') window.location.assign('/');
  } finally {
    if (soft) logoutInProgress = false;
  }
}

// --- refresh ---------------------------------------------------------------

async function attemptTokenRefresh(): Promise<RefreshResult> {
  if (refreshInProgress) return refreshInProgress;

  refreshInProgress = (async (): Promise<RefreshResult> => {
    if (!isCookieMode) {
      if (!tokenRefreshHandler) return 'failed';
      return (await tokenRefreshHandler()) ? 'ok' : 'failed';
    }
    try {
      const response = await fetch(`${API_BASE_URL}/auth/refresh`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'X-Session-Mode': 'cookie',
          'X-CSRF-Token': getCsrfToken(),
          ...(X_API_KEY ? { 'X-Api-Key': X_API_KEY } : {}),
        },
      });
      if (response.ok) return 'ok';
      try {
        if (JSON.parse(await response.text())?.error?.code === 'NO_REFRESH_TOKEN') return 'no_refresh';
      } catch {
        /* non-JSON */
      }
      return 'failed';
    } catch {
      return 'failed';
    }
  })().finally(() => {
    refreshInProgress = null;
  });

  return refreshInProgress;
}

// --- recovery screens -------------------------------------------------------

type Navigator = (() => void) | null;
let noNetworkNavigator: Navigator = null;
let serviceUnavailableNavigator: Navigator = null;
let serviceUnavailableActive = false;

export function setNoNetworkNavigator(fn: Navigator) {
  noNetworkNavigator = fn;
}
export function setServiceUnavailableNavigator(fn: Navigator) {
  serviceUnavailableNavigator = fn;
}
/** True once a 503 has sent the app to the service-unavailable screen. */
export function isServiceUnavailableActive(): boolean {
  return serviceUnavailableActive;
}

function goTo(path: string, navigator: Navigator) {
  if (typeof window === 'undefined' || window.location.pathname === path) return;
  if (navigator) navigator();
  else window.location.assign(path);
}

// --- request pipeline --------------------------------------------------------

function withLangParam(rawUrl: string): string {
  try {
    const lang = i18n.language;
    if (!isSupportedLanguage(lang)) return rawUrl;
    const u = new URL(rawUrl, typeof window !== 'undefined' ? window.location.origin : 'http://localhost');
    if (!u.searchParams.has('l')) u.searchParams.set('l', lang);
    return u.toString();
  } catch {
    return rawUrl;
  }
}

function buildHeaders(json: boolean): Record<string, string> {
  const headers: Record<string, string> = {};
  if (json) headers['Content-Type'] = 'application/json';

  if (isCookieMode) {
    headers['X-Session-Mode'] = 'cookie';
    // Sent on every request, not just writes: it doubles as the BE's
    // session-family discriminator when admin and consumer cookies coexist.
    const csrf = getCsrfToken();
    if (csrf) headers['X-CSRF-Token'] = csrf;
  } else if (jwt) {
    headers['Authorization'] = `Bearer ${jwt}`;
  }
  if (X_API_KEY) headers['X-Api-Key'] = X_API_KEY;
  if (activeProfileId) headers['X-Sub-Identity'] = activeProfileId;
  if (deviceId) headers['X-Device-Id'] = deviceId;
  if (isSupportedLanguage(i18n.language)) headers['Accept-Language'] = i18n.language;
  return headers;
}

type ResponseKind = 'json' | 'blob' | 'text';

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  /** JSON-serialised unless it is FormData. */
  body?: unknown;
  /** Query parameters; undefined / null / '' values are dropped, arrays are comma-joined. */
  query?: QueryParams;
  signal?: AbortSignal;
  keepalive?: boolean;
  responseType?: ResponseKind;
}

export type QueryValue = string | number | boolean | null | undefined | ReadonlyArray<string | number>;
export type QueryParams = Record<string, QueryValue>;

export function buildQuery(params?: QueryParams): string {
  if (!params) return '';
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === '') continue;
    if (Array.isArray(value)) {
      if (value.length) search.set(key, value.join(','));
    } else {
      search.set(key, String(value));
    }
  }
  const qs = search.toString();
  return qs ? `?${qs}` : '';
}

const AUTH_EXEMPT = ['/auth/refresh', '/auth/logout', '/device/logout'];

export async function request<T>(endpoint: string, options: RequestOptions = {}, isRetry = false): Promise<T> {
  const method = options.method ?? 'GET';
  const path = `${endpoint}${buildQuery(options.query)}`;
  const url = withLangParam(`${API_BASE_URL}${path}`);
  const isForm = typeof FormData !== 'undefined' && options.body instanceof FormData;

  let response: Response;
  try {
    response = await fetch(url, {
      method,
      headers: buildHeaders(!isForm && options.body !== undefined),
      body: options.body === undefined ? undefined : isForm ? (options.body as FormData) : JSON.stringify(options.body),
      signal: options.signal,
      keepalive: options.keepalive,
      ...(isCookieMode ? { credentials: 'include' as RequestCredentials } : {}),
    });
  } catch (err) {
    if ((err as Error)?.name !== 'AbortError') goTo('/no-network', noNetworkNavigator);
    throw err;
  }

  if (!response.ok) {
    const rawBody = await response.text();
    const parsed = parseErrorBody(response.status, rawBody);
    const authExempt = AUTH_EXEMPT.some((p) => endpoint.includes(p));

    // 503: the BE booted without a database (degraded mode). Nothing to
    // recover per-request — show the full-screen unavailable page.
    if (response.status === 503) {
      serviceUnavailableActive = true;
      goTo('/service-unavailable', serviceUnavailableNavigator);
    }

    // CSRF mismatch: /auth/refresh rotates the CSRF cookie, then retry once.
    if (response.status === 403 && !isRetry && !authExempt && !logoutInProgress && parsed.code === 'CSRF_INVALID') {
      const refreshed = await attemptTokenRefresh();
      if (refreshed === 'ok') return request<T>(endpoint, options, true);
      await triggerAuthFailureLogout(refreshed === 'no_refresh');
    }

    // 401: always try a refresh — its result, not the 401's code, decides
    // whether the session is recoverable. Pre-auth 401s (a wrong password on
    // /login) propagate to the caller untouched, and so does every 401 while a
    // logout is already running — the first failure starts the workflow once.
    if (response.status === 401 && !isRetry && !authExempt && !logoutInProgress && !endpoint.startsWith('/login')) {
      const refreshed = await attemptTokenRefresh();
      if (refreshed === 'ok') return request<T>(endpoint, options, true);
      if (useAuthStore.getState().isAuthenticated) {
        await triggerAuthFailureLogout(refreshed === 'no_refresh');
      }
    }

    const error = new ApiError({
      status: response.status,
      code: parsed.code,
      params: parsed.params,
      correlationId: parsed.correlationId,
      userMessage: userMessageFor(parsed),
      endpoint,
      method,
      rawBody,
    });
    // Never log a failed /log call: the logger ships through this same
    // pipeline, so logging its failure would recurse.
    if (endpoint !== '/log') {
      logError(error, `${method} ${endpoint} failed`, undefined, {
        status: response.status,
        endpoint,
        body: rawBody,
        correlationId: parsed.correlationId,
      });
    }
    throw error;
  }

  if (options.responseType === 'blob') {
    logTrace(`${method} ${endpoint} completed`, undefined, { status: response.status });
    return (await response.blob()) as T;
  }

  const text = await response.text();
  logTrace(`${method} ${endpoint} completed`, undefined, { status: response.status });
  if (options.responseType === 'text') return text as T;
  if (response.status === 204 || !text) return undefined as T;
  return JSON.parse(text) as T;
}

// --- verb helpers -------------------------------------------------------------

export function get<T>(endpoint: string, query?: QueryParams, signal?: AbortSignal): Promise<T> {
  return request<T>(endpoint, { method: 'GET', query, signal });
}
export function post<T>(endpoint: string, body?: unknown, query?: QueryParams): Promise<T> {
  return request<T>(endpoint, { method: 'POST', body: body ?? {}, query });
}
export function put<T>(endpoint: string, body?: unknown, query?: QueryParams): Promise<T> {
  return request<T>(endpoint, { method: 'PUT', body: body ?? {}, query });
}
export function patch<T>(endpoint: string, body: unknown): Promise<T> {
  return request<T>(endpoint, { method: 'PATCH', body });
}
export function del<T>(endpoint: string, options?: { keepalive?: boolean }): Promise<T> {
  return request<T>(endpoint, { method: 'DELETE', keepalive: options?.keepalive });
}
export function postFormData<T>(endpoint: string, formData: FormData): Promise<T> {
  return request<T>(endpoint, { method: 'POST', body: formData });
}
